
/**
 * Color utility functions for mathematical color calculations
 * Used to create proper gradients from base hex colors
 */

/**
 * Convert hex color to HSL
 */
export function hexToHsl(hex: string): [number, number, number] {
  // Remove the hash if present
  const cleanHex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
  const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number, l: number;

  l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

/**
 * Convert HSL to hex color
 */
export function hslToHex(h: number, s: number, l: number): string {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Calculate a lighter version of a hex color by increasing lightness
 * @param hex - Base hex color (e.g., "#3B82F6")
 * @param lightnessIncrease - Amount to increase lightness (0-100, default: 30)
 * @returns Lighter hex color
 */
export function calculateLighterColor(hex: string, lightnessIncrease: number = 30): string {
  const [h, s, l] = hexToHsl(hex);
  
  // Increase lightness, but cap at 100
  const newLightness = Math.min(100, l + lightnessIncrease);
  
  return hslToHex(h, s, newLightness);
}

/**
 * Generate gradient colors from a base hex color
 * @param baseHex - Base hex color from database
 * @returns Object with from and to colors for gradients
 */
export function generateGradientColors(baseHex: string) {
  const fromColor = baseHex;
  const toColor = calculateLighterColor(baseHex, 30);
  
  return {
    from: fromColor,
    to: toColor
  };
}
